/**
 * SIL-572 (I2-C5): buyer journey strip — Discover → Negotiate → Transact —
 * shown on the BUYING routes + /transactions. Pure route-matching and
 * count-derivation logic lives here (testable without mounting react-query
 * or the router), mirroring dashboard.tsx's deriveWorkQueue split.
 *
 * Segment sourcing (per the issue):
 *   Discover   — no real "saved/browse state" signal exists anywhere in the
 *                API today (grep confirms: no saved_listing/watchlist table),
 *                so this segment renders with NO count, not an invented one.
 *   Negotiate  — the buyer's own in-negotiation demand: marketplace offers
 *                it placed that are still open (offer.status IN
 *                ('pending','countered'), the buyer-visible pre-terminal
 *                states — see apps/api/src/routes/client/offers.ts) PLUS its
 *                own open direct offers (direct_offer.status = 'open').
 *                Both are real, queryable columns.
 *   Transact   — its own in-flight marketplace_transaction rows
 *                (lifecycleState NOT IN ('completed','cancelled'), the same
 *                predicate dashboard.tsx's WorkQueueOverview already uses
 *                for "Active transactions"), scoped to buyerOrgId so a
 *                dual-profile org's SELLING transactions don't inflate its
 *                BUYING journey count.
 */

export type JourneySegmentKey = 'discover' | 'negotiate' | 'transact';

export interface JourneySegmentDef {
  key: JourneySegmentKey;
  label: string;
  /** Where this segment's link lands. */
  to: string;
}

export const JOURNEY_SEGMENTS: JourneySegmentDef[] = [
  { key: 'discover', label: 'Discover', to: '/marketplace' },
  { key: 'negotiate', label: 'Negotiate', to: '/offers' },
  { key: 'transact', label: 'Transact', to: '/transactions' },
];

/** The BUYING nav group's routes (SIL-563) + /transactions (SIL-572 scope). */
const DISCOVER_ROUTES = ['/marketplace', '/inventory'];
// SIL-654: /quotes removed with the quote_request RFQ path — sealed auctions
// + direct_offer are Negotiate's signal now.
const NEGOTIATE_ROUTES = ['/offers', '/direct-offers'];
const TRANSACT_ROUTES = ['/transactions'];
const JOURNEY_ROUTES = [...DISCOVER_ROUTES, ...NEGOTIATE_ROUTES, ...TRANSACT_ROUTES];

function matchesRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** True on exactly /marketplace, /offers, /direct-offers, /inventory, /transactions (+ their sub-routes). */
export function isJourneyStripRoute(pathname: string): boolean {
  return JOURNEY_ROUTES.some((route) => matchesRoute(pathname, route));
}

/** Which segment `pathname` belongs to, or null off the journey entirely. */
export function activeJourneySegment(pathname: string): JourneySegmentKey | null {
  if (DISCOVER_ROUTES.some((route) => matchesRoute(pathname, route))) return 'discover';
  if (NEGOTIATE_ROUTES.some((route) => matchesRoute(pathname, route))) return 'negotiate';
  if (TRANSACT_ROUTES.some((route) => matchesRoute(pathname, route))) return 'transact';
  return null;
}

export interface JourneyOfferSignal {
  isOwnOffer: boolean;
  status: string;
}

export interface JourneyDirectOfferSignal {
  status: string;
}

export interface JourneyTransactionSignal {
  lifecycleState: string;
  buyerOrgId: string | null;
}

const OPEN_OFFER_STATUSES = new Set(['pending', 'countered']);
const CLOSED_TRANSACTION_STATES = new Set(['completed', 'cancelled']);

/**
 * Negotiate + Transact counts from the caller's own raw rows (Discover has
 * no count — see the module comment). `buyerOrgId` is the caller's own org
 * id, used to scope the transaction count to its BUYING side only.
 */
export function deriveJourneyCounts({
  offers,
  directOffers,
  transactions,
  buyerOrgId,
}: {
  offers: JourneyOfferSignal[];
  directOffers: JourneyDirectOfferSignal[];
  transactions: JourneyTransactionSignal[];
  buyerOrgId: string | null;
}): { negotiate: number; transact: number } {
  const openOwnOffers = offers.filter((o) => o.isOwnOffer && OPEN_OFFER_STATUSES.has(o.status)).length;
  const openDirectOffers = directOffers.filter((d) => d.status === 'open').length;

  const inFlightTransactions =
    buyerOrgId == null
      ? 0
      : transactions.filter(
          (t) => t.buyerOrgId === buyerOrgId && !CLOSED_TRANSACTION_STATES.has(t.lifecycleState),
        ).length;

  return {
    negotiate: openOwnOffers + openDirectOffers,
    transact: inFlightTransactions,
  };
}
